import { Suspense, lazy, useEffect } from 'react'

import type { GenAiPanelProps } from '@verbit-ai/ai-service-web'
import { EmbeddedSearchResult } from '@verbit-ai/file-floater'
import { Flex } from '@chakra-ui/react'
import { LoadingDotsIcon } from '@verbit-ai/icons-library'

import { useTranscriptHighLight } from 'src/contexts/TranscriptHighLightContext'
import { useTranscriptViewer } from 'src/contexts/TranscriptViewerProvider'
import { isJobLive } from 'src/models/TranscriptMetadata'
import { useFeatureFlag } from 'src/contexts/FeatureFlagContext'
import { getDeepLinkInconsistencyId, getEnv, getTranscriptionDocumentId, isMockActive, isPdfDisplayMode } from 'src/utils/env'
import { analyticsService } from 'src/services/AnalyticsService'
import { productAdoptionService } from 'src/services/ProductAdoptionService'
import { rollbarService } from 'src/services/RollbarService'
import { useFerretSearch } from 'src/contexts/FerretSearchWrapperProvider'
import { useIsJobFinished, useTranscriptMetadata } from 'src/contexts/Transcript/MetadataContext'
import {
    useInconsistenciesPanelRef,
    useMarkInconsistencySelected,
    useReportInconsistencies,
} from 'src/contexts/InlineInconsistenciesContext'

const GenAiPanel = lazy(() => import('@verbit-ai/ai-service-web').then((module) => ({ default: module.GenAiPanel })))

const environment = getEnv()

const shouldUseMocks = isMockActive()

/* Read once: the deep link describes how this page was opened, not something that changes under the user. */
const deepLinkInconsistencyId = getDeepLinkInconsistencyId()

const onMixpanelEvent = analyticsService.track.bind(analyticsService)
const onAppcuesEvent = productAdoptionService.track.bind(productAdoptionService)
const onRollbarError = rollbarService.error.bind(rollbarService)

const AiPanelSuspenseFallback = () => (
    <Flex align="center" justifyContent="center" h="100%" minH="200px" w="100%" flex={1} aria-busy="true" aria-label="Loading insights">
        <LoadingDotsIcon size={20} />
    </Flex>
)

type AiPanelWrapperProps = Pick<GenAiPanelProps, 'onAuthCheckCompleted' | 'isOpen' | 'onPanelSizeModeChange'> & {
    onExpand: (updateRef?: boolean) => void
    onCollapse: (updateRef?: boolean) => void
}

export const AiPanelWrapper = (props: AiPanelWrapperProps) => {
    const { isOpen, onAuthCheckCompleted, onPanelSizeModeChange, onExpand, onCollapse } = props

    const transcriptMetadata = useTranscriptMetadata()!
    const { fileFloaterRef, selectTextToHighlightByOffset } = useTranscriptHighLight()
    const isJobFinished = useIsJobFinished()
    const { ferretSearchRef, shouldShowFerretSearch } = useFerretSearch()
    const enableAbsoluteTimestamps = useFeatureFlag('absolute_timestamps')
    const isInlineInconsistenciesEnabled = useFeatureFlag('insights_inline_inconsistencies')
    const reportInconsistencies = useReportInconsistencies()
    const panelRef = useInconsistenciesPanelRef()
    const markInconsistencySelected = useMarkInconsistencySelected()
    const { stopAutoScrolling } = useTranscriptViewer()
    // The panel unmounts in translations, chromakey and for anonymous users; its list must go with it.
    useEffect(() => () => reportInconsistencies([]), [reportInconsistencies])
    const isDocumentViewMode = transcriptMetadata.viewMode === 'document'

    const toggleAIPanel = () => {
        if (isOpen) {
            onCollapse()
        } else {
            onExpand()
        }
    }

    const triggerFerretSearch = shouldShowFerretSearch ? ferretSearchRef?.current?.search : undefined

    const jobId = isDocumentViewMode ? getTranscriptionDocumentId() : String(transcriptMetadata.transcriptionJobId)

    // `inconsistencyUuid` arrives only for an inconsistency's root statement.
    const scrollToSection: NonNullable<GenAiPanelProps['scrollToSection']> = (
        offset,
        word,
        searchResults = [],
        highlightTurns,
        inconsistencyUuid,
    ) => {
        if (isPdfDisplayMode()) {
            fileFloaterRef.current?.highlightText(searchResults as EmbeddedSearchResult[])

            return
        }

        const turns = highlightTurns?.length ? highlightTurns : word ? [{ text: word }] : undefined
        if (!isInlineInconsistenciesEnabled) {
            selectTextToHighlightByOffset(offset, turns)

            return
        }

        // Like a marker click: leave a quote that is already in view where it is, else bring it in with its
        // paragraph's header (time, speaker, markers) rather than pinning the words to the top edge. Live text and
        // playing media would then scroll away from a quote left in place, so both stop.
        stopAutoScrolling()
        const highlightId = selectTextToHighlightByOffset(offset, turns, { reveal: 'ifHidden' })
        if (inconsistencyUuid && highlightId !== null) {
            markInconsistencySelected(inconsistencyUuid, highlightId)
        }
    }

    return (
        <Suspense fallback={<AiPanelSuspenseFallback />}>
            <GenAiPanel
                ref={panelRef}
                isOpen={isOpen}
                onOpenToggle={toggleAIPanel}
                onAuthCheckCompleted={onAuthCheckCompleted}
                customerId={String(transcriptMetadata.customerId)}
                transcriptJobId={jobId}
                isJobFinished={isJobFinished}
                isJobLive={isJobLive(transcriptMetadata)}
                environment={environment}
                useVerboseLogging
                useMocks={shouldUseMocks ? document.mocks.getInsightsApiResults() : false}
                shouldListenToWebSocket={!shouldUseMocks}
                onMixpanelEvent={onMixpanelEvent}
                onAppcuesEvent={onAppcuesEvent}
                onRollbarError={onRollbarError}
                vertical={transcriptMetadata.vertical as GenAiPanelProps['vertical']}
                scrollToSection={shouldUseMocks ? undefined : scrollToSection}
                getInsightText={triggerFerretSearch}
                panelPositioning="detached"
                viewMode={isDocumentViewMode ? 'document' : 'verbit_transcript'}
                enableAbsoluteTimestamps={enableAbsoluteTimestamps}
                onPanelSizeModeChange={onPanelSizeModeChange}
                deepLinkInconsistencyId={deepLinkInconsistencyId}
                // Passing it makes the panel load while closed, so only with the flag on.
                onInconsistenciesChange={isInlineInconsistenciesEnabled ? reportInconsistencies : undefined}
            />
        </Suspense>
    )
}
